import React from "react";
import { FaRegCircleQuestion } from "react-icons/fa6";
import { IoChatbubbleOutline } from "react-icons/io5";
import MarkdownRenderer from "../../../components/MarkdownRenderer";
import { QuestionItem } from "../../../types/form";
import type { FeedbackItemDetail } from "../../../API/feedbackAPI";
import CompositeFieldRenderer from "./CompositeFieldRenderer";
import SignatureFieldRenderer from "./SignatureFieldRenderer";
import ANumberFieldRenderer from "./ANumberFieldRenderer";
import EmailFieldRenderer from "./EmailFieldRenderer";
import DateFieldRenderer from "./DateFieldRenderer";
import SelectFieldRenderer from "./SelectFieldRenderer";
import TextFieldRenderer from "./TextFieldRenderer";
import CheckboxFieldRenderer from "./CheckboxFieldRenderer";
import RadioFieldRenderer from "./RadioFieldRenderer";
import PhoneFieldRenderer from "./PhoneFieldRenderer";
import SsnFieldRenderer from "./SsnFieldRenderer";
import UscisAccountFieldRenderer from "./UscisAccountFieldRenderer";
import StandardFieldRenderer from "./StandardFieldRenderer";
import WeightFieldRenderer from "./WeightFieldRenderer";
import CountryFieldRenderer from "./CountryFieldRenderer";
import { getValidationFormat } from "../../../utils/validationRules";

interface QuestionRendererProps {
  question: QuestionItem;
  formData: Record<string, any>;
  setFormData: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  handleChange: (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => void;
  handleMultipleEntryChange: (
    inputName: string,
    idx: number,
    value: any,
  ) => void;
  handleAddEntry: (inputName: string) => void;
  handleRemoveEntry: (inputName: string, entryIdx: number) => void;
  handleCompositeFieldChange: (
    inputName: string,
    compositeAnswer: Record<string, any>,
    fieldKey: string,
    value: any,
  ) => void;
  handleMultipleCompositeChange: (
    inputName: string,
    entryIdx: number,
    fieldKey: string,
    value: any,
  ) => void;
  validationErrors?: Record<string, string>;
  onOpenHintsWithHint?: (
    hint: { title?: string; content: string } | null,
  ) => void;
  hasFeedback?: boolean;
  onOpenFeedbackWithQuestion?: (questionId: number) => void;
  /** When true, inputs are disabled (e.g. feedback revision — only pending-note fields editable). */
  readOnly?: boolean;
  /** The currently active feedback item from prev/next navigation (for inline card). */
  activeFeedbackItem?: FeedbackItemDetail | null;
  /** Required to render signature-type fields. */
  applicationId?: number;
}

const QuestionRenderer: React.FC<QuestionRendererProps> = ({
  question,
  formData,
  setFormData,
  handleChange,
  handleMultipleEntryChange,
  handleAddEntry,
  handleRemoveEntry,
  handleCompositeFieldChange,
  handleMultipleCompositeChange,
  validationErrors,
  onOpenHintsWithHint,
  hasFeedback,
  onOpenFeedbackWithQuestion,
  readOnly = false,
  activeFeedbackItem,
  applicationId,
}) => {
  // Normalize Type
  const rawType = question.answerType || question.type || "text";
  const questionType = rawType.toLowerCase();

  // Determine ID/Name
  const inputName = question.name || `q_${question.id}`;
  const currentValue = formData[inputName];

  // Get validation error for this field
  // Backend might return error keyed by ID or name
  const errorMessage =
    validationErrors?.[inputName] ||
    validationErrors?.[question.id] ||
    validationErrors?.[`q_${question.id}`];

  // Determine Label (Use questionText if label is missing)
  const labelText = question.label || question.questionText || "Question";
  const questionTextContent = question.questionText;

  // Determine Hint/Help Text
  const hintText =
    question.instructions ||
    question.helpText ||
    question.originalQuestion?.instructions;

  // Determine if question is composite
  const isComposite = questionType === "composite";
  const isWeight = questionType === "weight";
  const isSignature = questionType === "signature";
  const isCountry = questionType === "country";
  const validationFormat = getValidationFormat(question);

  const compositeAnswer: Record<string, any> =
    isComposite && !question.allowMultipleAnswers
      ? typeof currentValue === "object" &&
        currentValue !== null &&
        !Array.isArray(currentValue)
        ? (currentValue as Record<string, any>)
        : {}
      : {};

  const weightAnswer: Record<string, any> =
    isWeight && !question.allowMultipleAnswers
      ? typeof currentValue === "object" &&
        currentValue !== null &&
        !Array.isArray(currentValue)
        ? (currentValue as Record<string, any>)
        : {}
      : {};

  const renderField = () => {
    if (isSignature && applicationId) {
      return (
        <SignatureFieldRenderer
          question={question}
          inputName={inputName}
          currentValue={currentValue}
          applicationId={applicationId}
          setFormData={setFormData}
          readOnly={readOnly}
        />
      );
    }
    if (isWeight && !question.allowMultipleAnswers) {
      return (
        <WeightFieldRenderer
          question={question}
          inputName={inputName}
          weightAnswer={weightAnswer}
          handleCompositeFieldChange={handleCompositeFieldChange}
          readOnly={readOnly}
        />
      );
    }
    if (isComposite) {
      return (
        <CompositeFieldRenderer
          question={question}
          inputName={inputName}
          currentValue={currentValue}
          compositeAnswer={compositeAnswer}
          handleCompositeFieldChange={handleCompositeFieldChange}
          handleMultipleCompositeChange={handleMultipleCompositeChange}
          handleAddEntry={handleAddEntry}
          handleRemoveEntry={handleRemoveEntry}
          readOnly={readOnly}
        />
      );
    }

    if (isCountry) {
      return (
        <CountryFieldRenderer
          question={question}
          inputName={inputName}
          currentValue={currentValue}
          setFormData={setFormData}
          handleMultipleEntryChange={handleMultipleEntryChange}
          handleAddEntry={handleAddEntry}
          handleRemoveEntry={handleRemoveEntry}
          readOnly={readOnly}
        />
      );
    }

    if (validationFormat === "us_ssn") {
      return (
        <SsnFieldRenderer
          question={question}
          inputName={inputName}
          currentValue={currentValue}
          setFormData={setFormData}
          readOnly={readOnly}
        />
      );
    }

    if (validationFormat === "us_phone") {
      return (
        <PhoneFieldRenderer
          question={question}
          inputName={inputName}
          currentValue={currentValue}
          setFormData={setFormData}
          readOnly={readOnly}
        />
      );
    }

    if (validationFormat === "us_a_number") {
      return (
        <ANumberFieldRenderer
          question={question}
          inputName={inputName}
          currentValue={currentValue}
          setFormData={setFormData}
          readOnly={readOnly}
        />
      );
    }

    if (validationFormat === "uscis_account") {
      return (
        <UscisAccountFieldRenderer
          question={question}
          inputName={inputName}
          currentValue={currentValue}
          setFormData={setFormData}
          readOnly={readOnly}
        />
      );
    }

    if (validationFormat === "email") {
      return (
        <EmailFieldRenderer
          question={question}
          inputName={inputName}
          currentValue={currentValue}
          setFormData={setFormData}
          readOnly={readOnly}
        />
      );
    }

    if (questionType === "date") {
      return (
        <DateFieldRenderer
          question={question}
          inputName={inputName}
          currentValue={currentValue}
          formData={formData}
          setFormData={setFormData}
          handleMultipleEntryChange={handleMultipleEntryChange}
          handleAddEntry={handleAddEntry}
          handleRemoveEntry={handleRemoveEntry}
          readOnly={readOnly}
        />
      );
    }

    if (["select", "dropdown"].includes(questionType)) {
      return (
        <SelectFieldRenderer
          question={question}
          inputName={inputName}
          currentValue={currentValue}
          setFormData={setFormData}
          readOnly={readOnly}
        />
      );
    }

    if (["radio", "yes_no", "boolean"].includes(questionType)) {
      return (
        <RadioFieldRenderer
          question={question}
          questionType={questionType}
          inputName={inputName}
          currentValue={currentValue}
          setFormData={setFormData}
          readOnly={readOnly}
          onOpenHintsWithHint={onOpenHintsWithHint}
        />
      );
    }

    if (["checkbox", "checkbox_group"].includes(questionType)) {
      return (
        <CheckboxFieldRenderer
          question={question}
          inputName={inputName}
          currentValue={currentValue}
          setFormData={setFormData}
          readOnly={readOnly}
          onOpenHintsWithHint={onOpenHintsWithHint}
        />
      );
    }

    if (
      ["text", "number", "textarea", "long_text"].includes(questionType)
    ) {
      return (
        <TextFieldRenderer
          question={question}
          questionType={questionType}
          inputName={inputName}
          currentValue={currentValue}
          setFormData={setFormData}
          handleChange={handleChange}
          handleMultipleEntryChange={handleMultipleEntryChange}
          handleAddEntry={handleAddEntry}
          handleRemoveEntry={handleRemoveEntry}
          readOnly={readOnly}
        />
      );
    }

    return (
      <StandardFieldRenderer
        question={question}
        questionType={questionType}
        inputName={inputName}
        currentValue={currentValue}
        formData={formData}
        setFormData={setFormData}
        handleChange={handleChange}
        handleMultipleEntryChange={handleMultipleEntryChange}
        handleAddEntry={handleAddEntry}
        handleRemoveEntry={handleRemoveEntry}
        onOpenHintsWithHint={onOpenHintsWithHint}
        readOnly={readOnly}
      />
    );
  };

  // "info" questions collect no answer: a lead-in shown above a group of
  // sub-questions (as on the printed form), or a notice shown on a condition.
  // validationRules.variant === "notice" renders it as a highlighted callout.
  if (questionType === "info") {
    const isNotice = question.validationRules?.variant === "notice";
    return (
      <div
        className="pb-6"
        id={`form-question-${question.id}`}
        data-question-id={question.id}
      >
        <div
          role={isNotice ? "note" : undefined}
          className={
            isNotice
              ? "rounded-lg border-l-4 border-[#E8A54B] bg-[#FFFBF5] px-4 py-3 text-base text-[#1B365D]"
              : "text-xl font-medium text-[#1B365D]"
          }
        >
          <MarkdownRenderer content={questionTextContent || labelText} />
          {isNotice && hintText && (
            <div className="mt-1 text-sm text-[#1B365D99]">
              <MarkdownRenderer content={hintText} />
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className="pb-6"
      id={`form-question-${question.id}`}
      data-question-id={question.id}
    >
      <div className="space-y-3">
        {/* Inline hint/description moved to Smart Form Guide sidebar — click help icon to view */}
        {/* {hintText && (
          <div className="text-sm tracking-wide text-[#1B365D99]">
            <MarkdownRenderer content={hintText} />
          </div>
        )} */}
        <div
          className={`block mb-2 text-xl font-medium ${errorMessage ? "text-red-600" : "text-[#1B365D]"} ${readOnly ? "opacity-80" : ""}`}
        >
          <div className="flex items-center gap-2">
            <MarkdownRenderer content={questionTextContent || labelText} />
            {question.isRequired && (
              <span className="text-red-500 ml-1">*</span>
            )}
            {hintText && onOpenHintsWithHint && (
              <button
                type="button"
                onClick={() =>
                  onOpenHintsWithHint({
                    title: labelText || question.questionText,
                    content: hintText,
                  })
                }
                className="text-gray-400 hover:text-[#1B365D] transition-colors shrink-0"
                aria-label="Show hint in sidebar"
              >
                <FaRegCircleQuestion className="text-lg" />
              </button>
            )}
            {hasFeedback && onOpenFeedbackWithQuestion && (
              <button
                type="button"
                onClick={() => onOpenFeedbackWithQuestion(question.id)}
                className="text-[#1B365D] hover:text-[#0f1e33] transition-colors shrink-0"
                aria-label="View feedback"
              >
                <IoChatbubbleOutline className="text-lg" />
              </button>
            )}
          </div>
        </div>
        {errorMessage && (
          <p className="text-red-600 text-sm mt-1" role="alert">
            {errorMessage}
          </p>
        )}
        {renderField()}
        {/* Mirrors FeedbackTab active item (prev/next or jump-to-question) without opening the drawer. */}
        {activeFeedbackItem?.questionId === question.id && (
          <div
            className="mt-3 rounded-lg border-l-4 border-[#E8A54B] bg-[#FFFBF5] px-4 py-3"
            role="note"
            aria-label="Reviewer note for this question"
          >
            <div className="flex items-center gap-2 mb-1.5">
              <div className="h-6 w-6 rounded-full bg-[#1B365D] text-white text-[10px] font-semibold flex items-center justify-center shrink-0">
                {(() => {
                  const email = activeFeedbackItem.adminEmail ?? "";
                  const local = email.split("@")[0] || "?";
                  const parts = local.replace(/[._]/g, " ").split(" ").filter(Boolean);
                  if (parts.length === 0) return "?";
                  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
                  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
                })()}
              </div>
              <span className="text-xs font-semibold text-[#B45309]">Reviewer note</span>
            </div>
            <p className="text-sm text-[#111827] whitespace-pre-wrap break-words leading-relaxed">
              {activeFeedbackItem.feedbackText}
            </p>
          </div>
        )}
        {/* Inline help text moved to Smart Form Guide sidebar */}
        {/* {question.helpText && !hintText && (
          <p className="text-sm text-gray-600 mt-1">{question.helpText}</p>
        )} */}
      </div>
    </div>
  );
};

export default QuestionRenderer;
